import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { IMPACT_METRICS } from './esgData';

const CATEGORY_COLORS = {
  Environmental: { bg: '#dcfce7', text: '#15803d' },
  Social: { bg: '#dbeafe', text: '#1d4ed8' },
  Governance: { bg: '#f3e8ff', text: '#7c3aed' },
};

const ASSETS_FILTER = ['All Assets', 'Mersey Tidal Array', 'Pennine Wind Farm', 'Anglian Water Treatment', 'Bristol Solar Park', 'Northern Rail Freight Hub', 'Thames Digital Corridor'];

function ProgressBar({ pct }) {
  const color = pct >= 90 ? '#16a34a' : pct >= 70 ? '#d97706' : '#dc2626';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden" style={{ minWidth: 60 }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
      <span className="font-caption text-xs" style={{ color, minWidth: 36 }}>{pct?.toFixed(0)}%</span>
    </div>
  );
}

export default function ImpactMetricsTable() {
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [verifiedFilter, setVerifiedFilter] = useState('All');
  const [assetFilter, setAssetFilter] = useState('All Assets');

  const filtered = IMPACT_METRICS?.filter((m) => {
    if (categoryFilter !== 'All' && m?.category !== categoryFilter) return false;
    if (verifiedFilter === 'Verified' && !m?.verified) return false;
    if (verifiedFilter === 'Pending' && m?.verified) return false;
    return true;
  });

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '0.9375rem' }}>Impact Metrics</h3>
          <p className="font-caption text-muted-foreground text-xs mt-0.5">Detailed performance against targets — sample data</p>
        </div>
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Category */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {['All', 'Environmental', 'Social', 'Governance']?.map((c) => (
              <button
                key={c}
                onClick={() => setCategoryFilter(c)}
                className="px-2.5 py-1 rounded-md font-caption text-xs transition-all"
                style={{
                  background: categoryFilter === c ? 'var(--color-card)' : 'transparent',
                  color: categoryFilter === c ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                  fontWeight: categoryFilter === c ? 600 : 400,
                  boxShadow: categoryFilter === c ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {c}
              </button>
            ))}
          </div>
          {/* Verified */}
          <select
            value={verifiedFilter}
            onChange={(e) => setVerifiedFilter(e?.target?.value)}
            className="border border-border rounded-lg px-2.5 py-1.5 font-caption text-xs bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option>All</option>
            <option>Verified</option>
            <option>Pending</option>
          </select>
          {/* Asset */}
          <select
            value={assetFilter}
            onChange={(e) => setAssetFilter(e?.target?.value)}
            className="border border-border rounded-lg px-2.5 py-1.5 font-caption text-xs bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {ASSETS_FILTER?.map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ fontSize: '0.8rem' }}>
          <thead>
            <tr className="border-b border-border">
              {['Metric', 'Category', 'Value', 'Prior Period', 'Change', 'Target', 'vs Target', 'Source', 'Verified']?.map((h) => (
                <th key={h} className="text-left pb-2.5 pr-3 font-caption text-muted-foreground font-medium whitespace-nowrap" style={{ fontSize: '0.72rem' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered?.map((m) => {
              const catStyle = CATEGORY_COLORS?.[m?.category] || { bg: '#f1f5f9', text: '#475569' };
              const isGoodChange = m?.lowerIsBetter ? m?.change < 0 : m?.change >= 0;
              const changeColor = isGoodChange ? '#16a34a' : '#dc2626';
              return (
                <tr key={m?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="py-2.5 pr-3 font-caption font-medium text-foreground whitespace-nowrap" style={{ fontSize: '0.8rem' }}>{m?.name}</td>
                  <td className="py-2.5 pr-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full font-caption text-xs" style={{ background: catStyle?.bg, color: catStyle?.text, fontSize: '0.7rem' }}>{m?.category}</span>
                  </td>
                  <td className="py-2.5 pr-3 font-caption font-semibold text-foreground whitespace-nowrap">{m?.value} <span className="text-muted-foreground font-normal" style={{ fontSize: '0.7rem' }}>{m?.unit}</span></td>
                  <td className="py-2.5 pr-3 font-caption text-muted-foreground whitespace-nowrap">{m?.prior}</td>
                  <td className="py-2.5 pr-3">
                    <span className="font-caption font-semibold text-xs" style={{ color: changeColor }}>
                      {m?.change > 0 ? '▲' : m?.change < 0 ? '▼' : '—'} {m?.change !== 0 ? `${Math.abs(m?.change)}%` : '0%'}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 font-caption text-muted-foreground whitespace-nowrap">{m?.target} <span style={{ fontSize: '0.7rem' }}>{m?.unit}</span></td>
                  <td className="py-2.5 pr-3" style={{ minWidth: 120 }}><ProgressBar pct={m?.vsTarget} /></td>
                  <td className="py-2.5 pr-3 font-caption text-muted-foreground whitespace-nowrap" style={{ fontSize: '0.75rem' }}>{m?.source}</td>
                  <td className="py-2.5">
                    {m?.verified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-caption text-xs" style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.7rem' }}>
                        <Icon name="CheckCircle" size={10} color="#15803d" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-caption text-xs" style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.7rem' }}>
                        <Icon name="Clock" size={10} color="#b45309" /> Pending
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered?.length === 0 && (
          <div className="text-center py-8 font-caption text-muted-foreground text-sm">No metrics match the selected filters.</div>
        )}
      </div>
    </div>
  );
}
